import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '@services/auth.service';
import { CommonService } from './common.service';
import { AlertService } from './alert.service';
import { AppService } from '@services/app.service';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class TokenInterceptorInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  private REFRESH_THRESHOLD_SEC = 5 * 60;

  constructor(
    private _authservice: AuthService,
    private commonService: CommonService,
    private router: Router,
    private _toastr: ToastrService,
    private appService: AppService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip login/signup requests
    if (request.url.includes('login') || request.url.includes('signup')) {
      return next.handle(request);
    }

    let token = this.commonService.getItem('access_token');

    if (token) {
      // Log token expiry
      this.logTokenExpiry(token);

      // ✅ Proactive refresh check
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload?.exp;
      const now = Date.now() / 1000;

      if (!exp || (exp - now) <= this.REFRESH_THRESHOLD_SEC) {
        // Token will expire soon → refresh first
        return this.handle401Error(request, next);
      }
    }

    const authReq = token
      ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : request;

    return next.handle(authReq).pipe(
      catchError(err => {
        // If server responds 401, refresh token
        if (err.status === 401) {
          return this.handle401Error(request, next);
        }
        return throwError(() => err);
      })
    );
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload?.exp;
      if (!exp) return true;

      const now = Date.now() / 1000;
      return now >= exp;
    } catch (e) {
      console.error('[Interceptor] Failed to decode token', e);
      return true;
    }
  }

  private logTokenExpiry(token: string) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload?.exp) {
        const expDate = new Date(payload.exp * 1000);
        const remainingSec = Math.floor(payload.exp - Date.now() / 1000);
        console.log('[Interceptor] Access token expiry:', expDate.toString(), `(${remainingSec}s left)`);
      } else {
        console.warn('[Interceptor] No exp in token payload');
      }
    } catch (e) {
      console.error('[Interceptor] Error decoding token expiry', e);
    }
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.commonService.getItem('refresh_token');
      if (!refreshToken) {
        this.logoutUser('Session expired. Please login again.');
        return throwError(() => 'No refresh token found');
      }

      return this._authservice.refreshToken(refreshToken).pipe(
        switchMap((res: any) => {
          this.isRefreshing = false;

          const newToken = res?.data?.access_token;
          if (!newToken) {
            this.logoutUser('Session expired. Please login again.');
            return throwError(() => 'No new access token');
          }

          this.commonService.setItem('access_token', newToken);
          this.refreshTokenSubject.next(newToken);

          // Retry original request with new token
          return next.handle(request.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }));
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.logoutUser('Session expired. Please login again.');
          return throwError(() => err);
        })
      );
    } else {
      // Queue requests if refresh in progress
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          return next.handle(request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }));
        })
      );
    }
  }

  private logoutUser(message: string) {
    console.warn('[Interceptor] ', message);
    this.commonService.removeItem('access_token');
    this.commonService.removeItem('refresh_token');
    this._toastr.error(message);
    this.appService.logout();
    this.router.navigate(['/login']); // force back to login page
  }

}
